
/**
 * Skincare Routine Finder - Main App
 * This is the entry point that initializes the application
 */

// Hide any error dialogs immediately before DOMContentLoaded, but only specific to our quiz
(function() {
  // Hide any error dialogs that might be already showing, but only target those within our quiz container
  if (document.readyState !== 'loading') {
    const quizContainer = document.querySelector('.skincare-quiz-container');
    if (quizContainer) {
      const quizErrorDialogs = quizContainer.querySelectorAll('.error-dialog, .main [role="dialog"]');
      quizErrorDialogs.forEach(dialog => {
        if (dialog.style.display !== 'none') {
          dialog.style.display = 'none';
        }
      });
    }
  }
})();

// Function to check if scripts are loaded
function areScriptsLoaded() {
  const hasShopifyHelpers = typeof window.shopifyHelpers !== 'undefined' && window.shopifyHelpers !== null;
  const hasResultsLogic = typeof window.resultsLogic !== 'undefined' && window.resultsLogic !== null;
  const hasFormQuestions = typeof window.formQuestions !== 'undefined' && window.formQuestions !== null;
  
  // Log the loading status of each script
  if (!hasShopifyHelpers) console.warn('shopifyHelpers not loaded');
  if (!hasResultsLogic) console.warn('resultsLogic not loaded');
  if (!hasFormQuestions) console.warn('formQuestions not loaded');
  
  return hasShopifyHelpers && hasResultsLogic && hasFormQuestions;
}

// Function to initialize the app
async function initializeApp() {
  console.log('App initialized successfully');
  
  // Hide any error dialogs that might be showing, but only within our quiz container
  const quizContainer = document.querySelector('.skincare-quiz-container');
  if (quizContainer) {
    const quizErrorDialogs = quizContainer.querySelectorAll('.error-dialog, [role="dialog"]');
    quizErrorDialogs.forEach(dialog => {
      if (dialog.style.display !== 'none') {
        dialog.style.display = 'none';
      }
    });
  }
  
  // Make sure all sections are hidden except the intro section
  const sections = document.querySelectorAll('.section');
  sections.forEach(section => {
    section.classList.remove('active');
  });
  
  // Check for stored recommendations
  let savedRecommendations = null;
  if (window.resultsLogic && typeof window.resultsLogic.getRoutineFromLocalStorage === 'function') {
    savedRecommendations = window.resultsLogic.getRoutineFromLocalStorage();
  }
  
  // Set quiz completion flag in localStorage when recommendations exist
  if (savedRecommendations) {
    localStorage.setItem('quizCompleted', 'true');
  }
  
  // Determine which section to show initially
  let initialSection = document.getElementById('introSection');
  
  // Show results directly if:
  // 1. We have saved recommendations AND
  // 2. Either URL has #results hash OR localStorage indicates quiz was completed previously
  if (savedRecommendations && 
      (window.location.hash === '#results' || localStorage.getItem('quizCompleted') === 'true')) {
    initialSection = document.getElementById('resultsSection');
    
    // If we're showing results directly, hide the "View your routine" link
    const viewRoutineLink = document.getElementById('viewRoutineLink');
    if (viewRoutineLink) {
      viewRoutineLink.style.display = 'none';
    }
  } else {
    // Only show 'View your routine' link if we have recommendations but aren't showing results
    const viewRoutineLink = document.getElementById('viewRoutineLink');
    if (viewRoutineLink && savedRecommendations) {
      viewRoutineLink.style.display = 'inline-block';
    } else if (viewRoutineLink) {
      viewRoutineLink.style.display = 'none';
    }
  }
  
  // Show the appropriate initial section
  if (initialSection) {
    initialSection.classList.add('active');
  }
  
  // Ensure products are loaded first 
  if (window.shopifyHelpers && typeof window.shopifyHelpers.ensureProductsLoaded === 'function') {
    try {
      const products = await window.shopifyHelpers.ensureProductsLoaded();
      console.log(`Products ensured to be loaded: ${products.length}`);
    } catch (error) {
      console.error('Error ensuring products are loaded:', error);
    }
  }
  // Also try loading products directly if the function exists
  else if (window.resultsLogic && typeof window.resultsLogic.loadProducts === 'function') {
    try {
      const products = await window.resultsLogic.loadProducts();
      console.log(`Products loaded in initializeApp: ${products.length}`);
    } catch (error) {
      console.error('Error loading products in initializeApp:', error);
    }
  }
  
  // Initialize the quiz sections
  if (typeof window.initializeQuiz === 'function') {
    window.initializeQuiz();
  } else {
    console.warn('Quiz initialization function not found, waiting for scripts to load');
    
    // Fallback initialization if main.js didn't load properly
    setTimeout(() => {
      if (typeof window.initializeQuiz === 'function') {
        window.initializeQuiz();
      } else {
        console.error('Quiz failed to initialize after waiting');
        // Load page anyway, don't show an error
        document.body.classList.add('quiz-loaded');
      }
    }, 1000);
  }
}

// Preload the shopifyHelpers-routinefinder.js script
function preloadShopifyHelpers() {
  if (typeof window.shopifyHelpers === 'undefined' || window.shopifyHelpers === null) {
    console.log('Preloading shopifyHelpers script...');
    
    const script = document.createElement('script');
    script.src = 'shopifyHelpers-routinefinder.js';
    script.async = true;
    script.onerror = () => console.error('Failed to load shopifyHelpers-routinefinder.js');
    script.onload = () => console.log('shopifyHelpers script loaded successfully');
    document.head.appendChild(script);
  }
}

// Preload the resultsLogic-routinefinder.js script
function preloadResultsLogic() {
  if (typeof window.resultsLogic === 'undefined' || window.resultsLogic === null) {
    console.log('Preloading resultsLogic script...');
    
    const script = document.createElement('script');
    script.src = 'resultsLogic-routinefinder.js';
    script.async = true;
    script.onerror = () => console.error('Failed to load resultsLogic-routinefinder.js');
    script.onload = () => console.log('resultsLogic script loaded successfully');
    document.head.appendChild(script);
  }
}

// Check scripts and initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  let attempts = 0;
  const maxAttempts = 10;
  
  // Try to preload critical scripts immediately
  preloadShopifyHelpers();
  preloadResultsLogic();
  
  function checkAndInitialize() {
    if (areScriptsLoaded()) {
      initializeApp();
    } else {
      attempts++;
      if (attempts < maxAttempts) {
        console.log(`Waiting for scripts to load... Attempt ${attempts}/${maxAttempts}`);
        
        // Try to load missing scripts again
        if (typeof window.shopifyHelpers === 'undefined' || window.shopifyHelpers === null) {
          preloadShopifyHelpers();
        }
        
        if (typeof window.resultsLogic === 'undefined' || window.resultsLogic === null) {
          preloadResultsLogic();
        }
        
        setTimeout(checkAndInitialize, 300);
      } else {
        console.error('Failed to load all required scripts after multiple attempts');
        // Initialize anyway, the app will use fallbacks for missing functionality
        initializeApp();
      }
    }
  }
  
  checkAndInitialize();
});

// Add event listener to handle the "OK" button on any error dialogs, but only within quiz sections
document.addEventListener('click', function(event) {
  // Only handle clicks within our quiz container
  if (!event.target.closest('.skincare-quiz-container')) {
    return;
  }
  
  if (event.target.textContent === 'OK' || 
      event.target.classList.contains('close-dialog') ||
      event.target.closest('[role="dialog"]')) {
    
    const dialog = event.target.closest('.main [role="dialog"]');
    if (dialog) {
      dialog.style.display = 'none';
    }
  }
});
